
<template>
  <main
    class="relative min-h-screen w-full flex items-center justify-center"
    style="background-color: #020308"
  >
    <img
      src="../../assets/images/foreplay-logo.png"
      class="fixed top-4 md:top-8 left-4 md:left-9 h-10 w-auto cursor-pointer"
      style="z-index: 110"
      @click="handleLogoClick"
    >
    <div class="team-invite-card">
      <div
        v-if="!teamInvite && !invalidInvite"
        class="w-full flex items-center justify-center"
        style="height: 180px;"
      >
        <BaseLoadingSpinnerCircle class="relative top-2 text-text-muted" />
      </div>

      <!-- ------------------- INVALID INVITE VIEW ------------------- -->
      <div
        v-else-if="invalidInvite"
        class="w-full flex flex-col justify-between items-center"
      >
        <div class="space-y-2">
          <BaseText
            type="label"
            size="lg"
            class="text-text-muted text-center"
          >
            Invalid or Expired Invitation
          </BaseText>
          <BaseText
            type="body"
            size="sm"
            class="text-text-subdued text-center max-w-xs flex-1"
          >
            This invite is no longer valid. Please contact your team admin to send a new one.
          </BaseText>
        </div>
        <BaseButton
          outlined
          class="w-full py-2.5 px-3 rounded-md flex items-center justify-center mt-5"
          @click="$router.push({ name: 'DiscoveryView' })"
        >
          <BaseText
            type="body"
            size="sm"
            class="text-text-subdued"
          >
            Go home
          </BaseText>
        </BaseButton>
      </div>

      <!-- ------------------- VALID INVITE VIEW ------------------- -->
      <div
        v-else
        class="w-full flex flex-col gap-1 fade-in"
      >
        <!-- Team Logo and Inviter Avatar -->
        <div
          class="relative mx-auto"
          style="width: 68px; height: 68px;"
        >
          <!-- Team Logo -->
          <div class="team-logo w-9 h-9 flex items-center justify-center p-2 rounded-md bg-neutral-900">
            <BaseText
              type="label"
              size="sm"
              class="text-white"
            >
              {{ teamInitial }}
            </BaseText>
          </div>
          <!-- Inviter Avatar -->
          <div class="inviter-avatar w-9 h-9 flex items-center justify-center p-2 rounded-full bg-neutral-50">
            <BaseText
              type="label"
              size="sm"
              class="text-text-muted"
            >
              {{ inviterInitial }}
            </BaseText>
          </div>
        </div>

        <BaseText
          type="label"
          size="md"
          class="text-text-muted text-center mt-4"
        >
          You're invited to join {{ teamInvite.teamName ? `'${teamInvite.teamName}'` : 'a team' }}
        </BaseText>
        <BaseText
          type="body"
          size="sm"
          class="text-text-normal text-center mt-1 mb-6"
        >
          {{ teamInvite.invitedBy || 'A user' }} has invited you to collaborate with the team.
        </BaseText>

        <!-- Accept/Decline View (Logged In) -->
        <TeamInviteAcceptDecline
          v-if="isAuthenticated && currentView === 'accept-decline'"
          :invite-id="token"
          :invite="teamInvite"
          :user-email="userEmail"
          @logout="handleInviteLogout"
        />

        <!-- Auth Forms (Not Logged In) -->
        <div
          v-else-if="!isAuthenticated"
          class="w-full flex flex-col"
        >
          <!-- Sign Up Form -->
          <TeamInviteSignUpForm
            v-if="currentView === 'signup'"
            :invite-email="teamInvite.toEmail"
            @auth-complete="handleAuthComplete"
          />

          <!-- Login Form -->
          <TeamInviteLoginForm
            v-else-if="currentView === 'login'"
            @auth-complete="handleAuthComplete"
          />

          <!-- Toggle between signup and login -->
          <div class="w-full flex items-center justify-center gap-1.5 mt-6">
            <BaseText
              type="body"
              size="sm"
              class="text-text-normal"
            >
              {{ currentView === 'signup' ? 'Already have an account?' : "Don't have an account?" }}
            </BaseText>
            <button
              class="text-primary-blue-800 transition-colors duration-100 hover:text-primary-blue-900"
              @click="toggleView"
            >
              <BaseText
                type="label"
                size="sm"
                class="text-primary-blue-800"
              >
                {{ currentView === 'signup' ? 'Log in' : 'Sign up' }}
              </BaseText>
            </button>
          </div>

          <!-- Divider -->
          <div class="w-full flex items-center gap-6 py-1 mt-6">
            <div class="flex-grow h-0 border-t border-border-normal" />
            <BaseText
              type="body"
              size="sm"
              class="text-text-normal"
            >
              or
            </BaseText>
            <div class="flex-grow h-0 border-t border-border-normal" />
          </div>

          <!-- Google Sign In Button -->
          <button
            class="google-btn w-full flex items-center justify-center gap-2.5 p-2 mt-4"
            :disabled="loadingGoogle"
            @click="handleGoogleSignIn"
          >
            <BaseLoadingSpinnerCircle
              v-if="loadingGoogle"
              class="text-text-muted"
            />
            <template v-else>
              <img
                src="../../assets/images/google-logo.png"
                class="w-5 h-5"
              >
              <BaseText
                type="heading"
                size="sm"
                class="text-text-muted"
              >
                {{ currentView === 'signup' ? 'Sign up' : 'Sign in' }} with Google
              </BaseText>
            </template>
          </button>

          <!-- Terms -->
          <BaseText
            type="body"
            size="xs"
            class="w-full text-center text-text-normal mt-5"
            style="max-width: 360px; margin-left: auto; margin-right: auto;"
          >
            By continuing you agree to our
            <a
              href="https://www.foreplay.co/page/terms-of-service"
              target="_blank"
              class="text-primary-blue-800 transition-colors duration-200 hover:text-primary-blue-900 underline"
            >Terms</a>
            and
            <a
              href="https://www.foreplay.co/page/privacy-policy"
              target="_blank"
              class="text-primary-blue-800 transition-colors duration-200 hover:text-primary-blue-900 underline"
            >Privacy Policy</a>
          </BaseText>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapGetters } from 'vuex'
import firebase from '@/api/config/FirebaseConfig'
import ForeplayAPI from '@/api/foreplayServer'
import TeamInviteAcceptDecline from '@/components/teamInvite/TeamInviteAcceptDecline.vue'
import TeamInviteSignUpForm from '@/components/teamInvite/TeamInviteSignUpForm.vue'
import TeamInviteLoginForm from '@/components/teamInvite/TeamInviteLoginForm.vue'

export default {
  name: 'TeamInviteView',
  components: {
    TeamInviteAcceptDecline,
    TeamInviteSignUpForm,
    TeamInviteLoginForm
  },
  data () {
    return {
      token: null,
      teamInvite: null,
      invalidInvite: false,
      currentView: 'signup', // 'signup', 'login', 'accept-decline'
      previousAuthView: 'signup',
      loadingGoogle: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['isUserLoggedIn', 'getUser']),
    isAuthenticated () {
      return this.isUserLoggedIn
    },
    userEmail () {
      return this.getUser?.email || firebase.auth().currentUser?.email || ''
    },
    inviterInitial () {
      return this.teamInvite?.invitedBy?.charAt(0)?.toUpperCase() || '?'
    },
    teamInitial () {
      return this.teamInvite?.teamName?.charAt(0)?.toUpperCase() || 'T'
    }
  },
  watch: {
    isAuthenticated (newVal) {
      if (newVal && this.teamInvite) {
        if (['signup', 'login'].includes(this.currentView)) {
          this.previousAuthView = this.currentView
        }
        this.currentView = 'accept-decline'
      } else if (!newVal && this.teamInvite) {
        this.currentView = this.previousAuthView
      }
    }
  },
  async mounted () {
    this.token = this.$route.params.token
    console.log(this.token)

    if (!this.token) {
      this.invalidInvite = true
      return
    }

    await this.getTeamInvite()

    // Check if user is already logged in
    if (this.isAuthenticated && this.teamInvite) {
      this.currentView = 'accept-decline'
    }
  },
  methods: {
    async getTeamInvite () {
      try {
        const { data: invitePreview } = await ForeplayAPI.Teams.getTeamInvitePreview(this.token)
        this.teamInvite = invitePreview
      } catch (error) {
        this.invalidInvite = true
        console.error(error)
      }
    },
    toggleView () {
      this.currentView = this.currentView === 'signup' ? 'login' : 'signup'
      this.previousAuthView = this.currentView
    },
    handleLogoClick () {
      if (this.isAuthenticated) {
        this.handleInviteLogout()
      }
    },
    async handleInviteLogout () {
      try {
        await firebase.auth().signOut()
        this.currentView = this.previousAuthView
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to log out. Please try again.'
        })
      }
    },
    async handleGoogleSignIn () {
      this.loadingGoogle = true
      try {
        const provider = new firebase.auth.GoogleAuthProvider()
        const result = await firebase.auth().signInWithPopup(provider)

        const isNewUser = result.additionalUserInfo.isNewUser

        if (isNewUser) {
          const userPayload = {
            first_name: result.additionalUserInfo.profile?.given_name || '',
            last_name: result.additionalUserInfo.profile?.family_name || '',
            email: result.user.email,
            name: result.user.displayName,
            trialStarted: Date.now(),
            onboardingStatus: 'TEAM'
          }
          await firebase.firestore().collection('auth-users').doc(result.user.uid).set(userPayload, { merge: true })
        }

        // Wait for onAuthStateChanged to hydrate the store
        await new Promise(resolve => setTimeout(resolve, 2000))

        if (this.isAuthenticated) {
          this.currentView = 'accept-decline'
          this.loadingGoogle = false
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          message: error.message || 'Google sign in failed.',
          type: 'error'
        })
        this.loadingGoogle = false
      }
    },
    async handleAuthComplete ({ isNewUser }) {
      // Wait for onAuthStateChanged to hydrate the store
      await new Promise(resolve => setTimeout(resolve, 2000))

      if (this.isAuthenticated) {
        this.currentView = 'accept-decline'
      }
    }
  }
}
</script>

<style scoped>
.team-invite-card{
  border-radius: 16px;
  padding: 26px;
  backdrop-filter: blur(40px);
  background: white;
  width: 480px;
}

.team-logo {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.inviter-avatar {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
}

.google-btn {
  border: 1px solid #E5E7EB;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: border-color 150ms ease-in-out, background-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.google-btn:hover:not(:disabled) {
  border-color: #D1D5DB;
  background-color: #F9FAFB;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.google-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
